import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import type { ExpandedFieldConfig } from '../Table';
import { ReferralData } from '../types';
import PatientInfoGrid, { type CdpProfileSyncTriples } from './PatientInfoGrid';
import EnrollmentSpecificInformationBlock from '../EnrollmentSpecificInformationBlock';
import {
  DisplayFieldItem,
  PatientProfileTabFields,
} from 'components/LeadManagement/LeadManagement.types';

/** Mirrors PatientInfoGrid defaults minus fields that belong on Lead rows only. */
const DEFAULT_PERSONAL_FIELDS_NO_LEAD_KEYS: Array<{ key: string; label: string }> = [
  { key: 'firstName', label: 'First Name' },
  { key: 'lastName', label: 'Last Name' },
  { key: 'email', label: 'Email' },
  { key: 'phoneNumber', label: 'Phone Number' },
  { key: 'address', label: 'Address' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'zipcode', label: 'Zipcode' },
  { key: 'dateOfBirth', label: 'Date of Birth' },
  { key: 'gender', label: 'Gender' },
  { key: 'age', label: 'Age' },
];

const mapDisplayFields = (items: DisplayFieldItem[] = []) => {
  return items.map((item) => ({
    key: item.fields.DisplayFieldsKey?.value || '',
    label: item.fields.DisplayFieldsLabel?.value || '',
  }));
};

function personalFieldConfigForFullProfile(expandedConfig?: ExpandedFieldConfig) {
  const fromSitecore = mapDisplayFields(expandedConfig?.ExpandedDisplayFields);
  return fromSitecore && fromSitecore.length > 0
    ? fromSitecore
    : DEFAULT_PERSONAL_FIELDS_NO_LEAD_KEYS;
}

export interface ProcessedFullProfileExpandedSectionsProps {
  row: ReferralData;
  onSendToCC?: () => void;
  isSending?: boolean;
  selectedDedupeRecord?: string;
  expandedConfig?: ExpandedFieldConfig;
  statusValue?: string;
  onFlagPatient?: () => Promise<void>;
  currentUser?: string;
  lockedRecordsCount?: number;
  isLockingEnabled?: boolean;
  currentTabKey?: string;
  PatientProfileConfig?: PatientProfileTabFields;
  caregiverHeaderText?: string;
}

const ProcessedFullProfileExpandedSections: React.FC<ProcessedFullProfileExpandedSectionsProps> = ({
  row,
  onSendToCC,
  isSending,
  selectedDedupeRecord,
  expandedConfig,
  statusValue,
  onFlagPatient,
  currentUser,
  lockedRecordsCount = 0,
  isLockingEnabled,
  currentTabKey,
  PatientProfileConfig,
  caregiverHeaderText,
}) => {
  const leadDetails = row.leadDetails ?? [];
  const allLeadIdsTooltip = useMemo(
    () => leadDetails.map((l) => l.leadId).join(', '),
    [leadDetails]
  );

  const personalFieldsFiltered = useMemo(
    () => personalFieldConfigForFullProfile(PatientProfileConfig),
    [PatientProfileConfig]
  );

  /** Parallel triples per leadDetail for GetSyncedResources batch (Profile detail only). */
  const cdpProfileSyncTriples = useMemo((): CdpProfileSyncTriples | undefined => {
    const guestRefs: string[] = [];
    const enrollmentIds: string[] = [];
    const leadIds: number[] = [];
    for (const d of leadDetails) {
      const g = d.guestRef != null ? String(d.guestRef).trim() : '';
      const e = d.enrollmentId != null ? String(d.enrollmentId).trim() : '';
      const lid = d.leadId;
      if (g && e && lid != null && Number.isFinite(Number(lid))) {
        guestRefs.push(g);
        enrollmentIds.push(e);
        leadIds.push(Number(lid));
      }
    }
    if (guestRefs.length === 0) return undefined;
    return { guestRefs, enrollmentIds, leadIds };
  }, [leadDetails]);

  return (
    <Box sx={{ mb: 1 }}>
      <PatientInfoGrid
        row={row}
        onSendToCC={onSendToCC}
        isSending={isSending}
        selectedDedupeRecord={selectedDedupeRecord}
        isProcessedTab
        fieldConfig={personalFieldsFiltered}
        personalInfoHeaderText={expandedConfig?.personalInfoHeaderText}
        showPersonalTitle
        infoTooltip={allLeadIdsTooltip || undefined}
        statusValue={statusValue}
        viewDetails={
          expandedConfig?.viewDetails
            ? {
                ...expandedConfig.viewDetails,
                text: PatientProfileConfig?.ViewDetails?.value?.text || 'Patient Profile View',
                href: PatientProfileConfig?.ViewDetails?.value?.href || '#',
              }
            : undefined
        }
        sendToCC={expandedConfig?.sendToCC}
        onFlagPatient={onFlagPatient}
        currentUser={currentUser}
        lockedRecordsCount={lockedRecordsCount}
        isLockingEnabled={isLockingEnabled}
        currentTabKey={currentTabKey}
        cdpProfileSyncTriples={cdpProfileSyncTriples}
        cdpUiMode="profile"
      />

      <EnrollmentSpecificInformationBlock
        leadDetails={leadDetails}
        leadCaregivers={row.leadCaregivers}
        enrollmentFieldConfig={PatientProfileConfig?.EnrollmentSpecificInformation}
        enrollmentHeaderText={PatientProfileConfig?.EnrollmentSpecificInformationHeaderText?.value}
        leadHeadingText={PatientProfileConfig?.LeadHeading?.value}
        trackingFieldConfig={PatientProfileConfig?.TrackingParams || expandedConfig?.trackingParams}
        trackingHeaderText={expandedConfig?.trackingParamsHeaderText}
        caregiverHeaderText={caregiverHeaderText}
        caregiverFieldConfig={PatientProfileConfig?.CaregiverInfo}
      />
    </Box>
  );
};

export default ProcessedFullProfileExpandedSections;
